import type { Ga4Item, Ga4Promotion, ListIdentity, OrderItem } from '@/types';
import {
  orderItemToGa4Item,
  orderItemToLegacyProduct,
  orderItemToShippingInfoProduct,
} from '@/utils/toGa4Item';
import { useCallback } from 'react';

// hydration has no currency field yet; centralize the one default
// here (was hardcoded in 5 call sites). Read from #checkoutData when the
// backend adds it.
const CURRENCY = 'EUR';

// GA4 rejects oversized list payloads; 2894 fixes the ceiling at 200. Applied
// centrally here so it cannot drift between the six list surfaces.
const MAX_LIST_ITEMS = 200;

// Spread into list payloads so an absent content id omits the key entirely.
const listIdFields = (id?: string) => (id === undefined ? {} : { item_list_id: id });

const cartValue = (items: Ga4Item[]) =>
  items.reduce((sum, i) => sum + (i.price ?? 0) * (i.quantity ?? 1), 0);

// Extend Window interface to include dataLayer and consent queue
declare global {
  interface Window {
    dataLayer?: any[];
    pushEcommerce?: (obj: Record<string, any>) => void;
  }
}

export const useDataLayer = () => {
  /**
   * Push data via the consent queue so all events land after OneTrust
   * has published consent and GA4 Config has fired.
   */
  const push = useCallback((data: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.pushEcommerce) {
      window.pushEcommerce(data);
      console.debug('[DataLayer] Event queued/pushed:', data);
    } else if (typeof window !== 'undefined' && window.dataLayer) {
      // Fallback if queue script hasn't loaded (shouldn't happen)
      window.dataLayer.push(data);
      console.debug('[DataLayer] Event pushed (no queue):', data);
    } else {
      console.debug(
        '[DataLayer] dataLayer not available, event not pushed:',
        data,
      );
    }
  }, []);

  // --- Two payload families, ONE message shape.
  //
  // GA4-native events (view_item, view_item_list, select_item, view_cart,
  // begin_checkout, promotions) carry the GA4 payload — `currency`, `value`,
  // `items`.
  //
  // The five events that predate the GA4 refactor (addToCart, removeFromCart,
  // addPaymentInfo, addShippingInfo, addDiscountCode) keep the LEGACY UA
  // Enhanced Ecommerce payload only — `currencyCode`, `add`/`remove.products`,
  // `paymentType`, `shippingTier`, `code` — matching commit 08b08753 exactly.
  // Client requirement: no GA4 keys (`currency`, `value`, `items`) on these five.
  //
  // The payload is only half the contract; the MESSAGE SHAPE is the other half.
  // Every event — both families — is now ONE message with no `{ ecommerce: null }`
  // reset in front of it.
  //
  // Why the reset is gone. It is a GA4-era convention: GTM merges `ecommerce`
  // recursively, so GA4 events null it first to stop a previous event's `items`
  // bleeding into the next. But that extra message leaves `ecommerce === null`
  // in the model between events, and this container's variables were written
  // when `ecommerce` was never null — an unguarded `{{DLV - ecommerce}}.add.products`
  // throws a TypeError on it, which GTM reports as "Variable returned false".
  // The legacy five never had the reset (`1fa0371fb` restored their payload but
  // left them routed through the GA4 helper, reintroducing it); this extends the
  // same known-good message shape to every event.
  //
  // Trade-off, deliberately accepted: without the reset a later event inherits
  // earlier keys, so a removeFromCart after an addToCart still carries the old
  // `add.products` alongside its own `remove.products`, and a view_item_list
  // after a view_item carries the previous `items` unless it sets its own. That
  // is exactly how the container behaved pre-GA4, and its "products array
  // (merged)" variable is built for it. Every helper below sets the keys its own
  // tags read, so a tag firing on its own event trigger still sees fresh data.

  const pushEvent = useCallback(
    (event: string, ecommerce: Record<string, unknown>) => {
      push({ event, ecommerce });
    },
    [push],
  );

  /**
   * `addToCart` — a product was added to the basket.
   */
  const addToCart = useCallback(
    (
      item: OrderItem,
      quantity?: number,
      _listName?: string,
      displayedPrice?: number,
    ) => {
      // `_listName` is accepted but NOT emitted: the legacy UA product shape has
      // no list field and the GTM container defines no variable for one. Kept in
      // the signature so call sites (useAddToBasket) stay unchanged if the
      // container later gains a `list` variable.
      pushEvent('addToCart', {
        currencyCode: CURRENCY,
        add: {
          products: [
            orderItemToLegacyProduct(item, quantity, displayedPrice),
          ],
        },
      });
    },
    [pushEvent],
  );

  /**
   * `removeFromCart` — a product was removed from the basket.
   */
  const removeFromCart = useCallback(
    (item: OrderItem) => {
      pushEvent('removeFromCart', {
        currencyCode: CURRENCY,
        remove: { products: [orderItemToLegacyProduct(item)] },
      });
    },
    [pushEvent],
  );

  /**
   * `view_cart` — the basket page was viewed.
   */
  const viewCart = useCallback(
    (items: OrderItem[]) => {
      const ga4Items = items.map(orderItemToGa4Item);
      pushEvent('view_cart', {
        currency: CURRENCY,
        value: cartValue(ga4Items),
        items: ga4Items,
      });
    },
    [pushEvent],
  );

  /**
   * `begin_checkout` — the checkout flow was entered.
   */
  const beginCheckout = useCallback(
    (items: OrderItem[]) => {
      const ga4Items = items.map(orderItemToGa4Item);
      pushEvent('begin_checkout', {
        currency: CURRENCY,
        value: cartValue(ga4Items),
        items: ga4Items,
      });
    },
    [pushEvent],
  );

  /**
   * `addPaymentInfo` — user selected a payment method.
   */
  const addPaymentInfo = useCallback(
    (paymentType: string, items: OrderItem[]) => {
      pushEvent('addPaymentInfo', {
        currencyCode: CURRENCY,
        paymentType,
        add: { products: items.map(i => orderItemToLegacyProduct(i)) },
      });
    },
    [pushEvent],
  );

  /**
   * `addShippingInfo` — user selected a shipping/delivery option.
   *
   * The only event whose products carry the category levels as separate
   * `item_category`…`item_category5` properties INSTEAD of the `category`
   * array, so the container reads a level as a plain string. Every other
   * legacy event keeps the array.
   */
  const addShippingInfo = useCallback(
    (shippingTier: string, items: OrderItem[]) => {
      pushEvent('addShippingInfo', {
        currencyCode: CURRENCY,
        shippingTier,
        add: { products: items.map(orderItemToShippingInfoProduct) },
      });
    },
    [pushEvent],
  );

  /**
   * `addDiscountCode` — a discount/coupon code was successfully applied.
   * Custom event (not GA4 standard); carries the legacy `code` key.
   */
  const addDiscountCode = useCallback(
    (code: string) => {
      pushEvent('addDiscountCode', { code });
    },
    [pushEvent],
  );

  /**
   * `view_promotion` — one or more promo tiles became visible.
   */
  const viewPromotion = useCallback(
    (promotions: Ga4Promotion[]) => {
      if (promotions.length === 0) {
        return;
      }
      pushEvent('view_promotion', { items: promotions });
    },
    [pushEvent],
  );

  /**
   * `select_promotion` — a promo tile (or its CTA) was clicked.
   */
  const selectPromotion = useCallback(
    (promotion: Ga4Promotion) => {
      pushEvent('select_promotion', { items: [promotion] });
    },
    [pushEvent],
  );

  const dismissPromotion = useCallback(
    (promotion: Ga4Promotion) => {
      pushEvent('dismiss_promotion', { items: [promotion] });
    },
    [pushEvent],
  );

   /**
   * `ui_expand` — an accordion / disclosure / "show more" was toggled.
   */
  const uiExpand = useCallback(
    (component: string, label: string, action: 'expand' | 'collapse') =>
      push({ event: 'ui_expand', component, label, action }),
    [push],
  );

  /**
   * `filter_apply` — a facet/refinement was applied on a listing page.
   */
  const filterApply = useCallback(
    (filterType: string, filterValue: string) =>
      push({ event: 'filter_apply', filter_type: filterType, filter_value: filterValue }),
    [push],
  );

  /**
   * `media_interact` — a media element was navigated (carousel page change,
   * video play/pause). `mediaType` identifies the surface (e.g.
   * `product-carousel`), `action` the gesture (`navigate`).
   */
  const mediaInteract = useCallback(
    (mediaType: string, action: string, index?: number) =>
      push({ event: 'media_interact', media_type: mediaType, action, index }),
    [push],
  );

  /**
   * `form_interact` — a form field was completed or errored. Carries the field
   * NAME only, never its value (PII).
   */
  const formInteract = useCallback(
    (formName: string, field: string, action: string) =>
      push({ event: 'form_interact', form_name: formName, field, action }),
    [push],
  );

  /**
   * `view_item` — a product detail page was viewed.
   */
  const viewItem = useCallback(
    (item: Ga4Item) => {
      // GA4 requires `currency` and `value` together on view_item, and the GTM
      // builders for Meta/Klaviyo read currency off the same object.
      pushEvent('view_item', {
        currency: CURRENCY,
        value: cartValue([item]),
        items: [item],
      });
    },
    [pushEvent],
  );

  /**
   * `view_item_list` — a product rail/grid became visible.
   */
  const viewItemList = useCallback(
    (list: ListIdentity, items: Ga4Item[]) => {
      if (items.length === 0) {
        return;
      }
      const capped = items.slice(0, MAX_LIST_ITEMS);
      // GA4 defines no 'value' for list events — only 'currency', so the
      // downstream Meta/Klaviyo builders can price the items themselves.
      pushEvent('view_item_list', {
        currency: CURRENCY,
        item_list_name: list.name,
        ...listIdFields(list.id),
        items: capped.map(item => ({
          item_list_name: list.name,
          ...listIdFields(list.id),
          ...item,
        })),
      });
    },
    [pushEvent],
  );

  /**
   * `select_item` — a product tile in a list was clicked.
   */
  const selectItem = useCallback(
    (list: ListIdentity, item: Ga4Item, index?: number) => {
      pushEvent('select_item', {
        currency: CURRENCY,
        item_list_name: list.name,
        ...listIdFields(list.id),
        items: [
          { item_list_name: list.name, ...listIdFields(list.id), index, ...item },
        ],
      });
    },
    [pushEvent],
  );

  return {
    push,
    addToCart,
    addDiscountCode,
    addPaymentInfo,
    addShippingInfo,
    removeFromCart,
    viewCart,
    beginCheckout,
    viewPromotion,
    selectPromotion,
    dismissPromotion,
    viewItem,
    viewItemList,
    selectItem,
    uiExpand,
    filterApply,
    mediaInteract,
    formInteract,
  };
};